import { brandStorage as localStorage } from '@/utils/brandSession';
import React, { useState, useEffect, useRef } from 'react';
import { Card } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import {
  Ticket,
  Send,
  Plus,
  Building2,
  Clock,
  CheckCircle,
  AlertTriangle,
  Pause,
  ChevronRight,
  MessageSquare,
  Image,
  X,
  Loader2,
  Users,
  Building,
  RefreshCw,
  Camera,
  Upload,
  XCircle,
  RotateCcw,
  Archive,
  Megaphone
} from 'lucide-react';
import { toast } from 'sonner';
import { API } from '../../utils/config';
import { openAttachment } from '../../utils/openAttachment';

const ApriTicket = () => {
  const [tickets, setTickets] = useState([]);
  const [properties, setProperties] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedTicket, setSelectedTicket] = useState(null);
  const [ticketMessages, setTicketMessages] = useState([]);
  
  // Tab attivo: aperti/in lavorazione vs chiusi
  const [activeTab, setActiveTab] = useState('aperti');
  
  // Filtri e ordinamento
  const [searchTerm, setSearchTerm] = useState('');
  const [filterStatus, setFilterStatus] = useState('');
  const [filterProperty, setFilterProperty] = useState('');
  const [filterCategory, setFilterCategory] = useState('');
  const [sortConfig, setSortConfig] = useState({ key: 'created_at', direction: 'desc' });
  const [viewMode, setViewMode] = useState('list'); // 'list' o 'table'
  
  // Form nuovo ticket
  const [showNewTicket, setShowNewTicket] = useState(false);
  const [newTicket, setNewTicket] = useState({
    property_id: '',
    subject: '',
    message: '',
    priority: 'normale',
    recipients: ['ufficio'], // Default: ufficio selezionato
    category: '',  // Categoria ticket
    attachments: []
  });
  const [sendingTicket, setSendingTicket] = useState(false);
  const [propertySearch, setPropertySearch] = useState('');
  const [selectedProperty, setSelectedProperty] = useState(null);
  const [categories, setCategories] = useState([]);
  
  // Risposta
  const [replyContent, setReplyContent] = useState('');
  const [replyAttachments, setReplyAttachments] = useState([]);
  const [replyRecipients, setReplyRecipients] = useState([]);
  const [sendingReply, setSendingReply] = useState(false);
  
  const fileInputRef = useRef(null);
  const cameraInputRef = useRef(null);
  const replyFileInputRef = useRef(null);
  const replyCameraInputRef = useRef(null);
  const token = localStorage.getItem('hw_token');

  // Categorie ticket
  const TICKET_CATEGORIES = {
    clima: { label: "Clima/Riscaldamento", color: "blue" },
    impianti: { label: "Impianti (Wifi, elettrico)", color: "blue" },
    comfort: { label: "Comfort (arredi, rumore)", color: "blue" },
    dotazioni: { label: "Dotazioni mancanti", color: "blue" },
    manutenzione: { label: "Manutenzione/Guasti", color: "blue" },
    checkin: { label: "Check-in/Check-out", color: "green" },
    pulizia: { label: "Pulizia alloggio", color: "green" },
    ospite: { label: "Problemi con ospite", color: "green" },
    pagamenti: { label: "Pagamenti/Fatture", color: "purple" },
    comunicazioni: { label: "Comunicazioni/Info", color: "purple" },
    sistema: { label: "Problemi sistema", color: "purple" },
    altro: { label: "Altro", color: "gray" }
  };

  // Bucket di appartenenza del ticket: comunicazioni ufficio / aperti / chiusi
  const bucketOf = (t) => (t.is_communication ? 'comunicazioni' : (t.status === 'chiuso' ? 'chiusi' : 'aperti'));

  // Filtra e ordina ticket
  const filteredTickets = React.useMemo(() => {
    let result = [...tickets];
    
    // Tab filter: aperti / chiusi / comunicazioni ufficio
    result = result.filter(t => bucketOf(t) === activeTab);
    
    if (searchTerm) {
      const term = searchTerm.toLowerCase();
      result = result.filter(t =>
        t.subject?.toLowerCase().includes(term) ||
        t.property_name?.toLowerCase().includes(term)
      );
    }
    
    if (filterStatus) {
      result = result.filter(t => t.status === filterStatus);
    }
    if (filterProperty) {
      result = result.filter(t => t.property_id === filterProperty);
    }
    if (filterCategory) {
      result = result.filter(t => t.category === filterCategory);
    }
    
    result.sort((a, b) => {
      let aVal = a[sortConfig.key];
      let bVal = b[sortConfig.key];
      if (sortConfig.key === 'created_at') {
        aVal = new Date(aVal || 0).getTime();
        bVal = new Date(bVal || 0).getTime();
      }
      if (typeof aVal === 'string') aVal = aVal.toLowerCase();
      if (typeof bVal === 'string') bVal = bVal.toLowerCase();
      if (aVal < bVal) return sortConfig.direction === 'asc' ? -1 : 1;
      if (aVal > bVal) return sortConfig.direction === 'asc' ? 1 : -1;
      return 0;
    });
    
    return result;
  }, [tickets, activeTab, searchTerm, filterStatus, filterProperty, filterCategory, sortConfig]);

  const handleSort = (key) => {
    setSortConfig(prev => ({
      key,
      direction: prev.key === key && prev.direction === 'asc' ? 'desc' : 'asc'
    }));
  };

  const getCategoryBadge = (category) => {
    const cat = TICKET_CATEGORIES[category];
    if (!cat) return null;
    const colorClasses = {
      blue: 'bg-blue-100 text-blue-700',
      green: 'bg-green-100 text-green-700',
      purple: 'bg-purple-100 text-purple-700',
      gray: 'bg-gray-100 text-gray-600'
    };
    return (
      <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs ${colorClasses[cat.color] || colorClasses.gray}`}>
        {cat.label}
      </span>
    );
  };

  const handleOpenNewTicket = () => {
    setNewTicket({
      property_id: '',
      subject: '',
      message: '',
      priority: 'normale',
      recipients: ['ufficio'],
      category: '',
      attachments: []
    });
    setPropertySearch('');
    setSelectedProperty(null);
    setShowNewTicket(true);
  };

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    setLoading(true);
    try {
      // Carica immobili
      const propsRes = await fetch(`${API}/api/responsible/my-properties`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (propsRes.ok) {
        setProperties(await propsRes.json());
      }

      // Carica ticket
      const ticketsRes = await fetch(`${API}/api/tickets`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (ticketsRes.ok) {
        setTickets(await ticketsRes.json());
      }
      
      // Carica categorie
      const catRes = await fetch(`${API}/api/tickets/categories`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (catRes.ok) {
        const data = await catRes.json();
        setCategories(data.categories || []);
      }
    } catch (error) {
      console.error('Error:', error);
      toast.error('Errore nel caricamento');
    } finally {
      setLoading(false);
    }
  };

  const handleImageUpload = (e, isReply = false) => {
    const files = Array.from(e.target.files);
    const maxFiles = 5;
    
    files.slice(0, maxFiles).forEach(file => {
      if (!file.type.startsWith('image/')) {
        toast.error('Solo immagini sono permesse');
        return;
      }
      if (file.size > 5 * 1024 * 1024) {
        toast.error('Immagine troppo grande (max 5MB)');
        return;
      }
      
      const reader = new FileReader();
      reader.onloadend = () => {
        if (isReply) {
          setReplyAttachments(prev => [...prev.slice(0, maxFiles - 1), reader.result]);
        } else {
          setNewTicket(prev => ({
            ...prev,
            attachments: [...prev.attachments.slice(0, maxFiles - 1), reader.result]
          }));
        }
      };
      reader.readAsDataURL(file);
    });
  };

  const removeAttachment = (index, isReply = false) => {
    if (isReply) {
      setReplyAttachments(prev => prev.filter((_, i) => i !== index));
    } else {
      setNewTicket(prev => ({
        ...prev,
        attachments: prev.attachments.filter((_, i) => i !== index)
      }));
    }
  };

  const handleCreateTicket = async (e) => {
    e.preventDefault();
    if (!newTicket.property_id || !newTicket.subject || !newTicket.message) {
      toast.error('Compila tutti i campi richiesti');
      return;
    }
    
    if (newTicket.recipients.length === 0) {
      toast.error('Seleziona almeno un destinatario');
      return;
    }

    setSendingTicket(true);
    try {
      const res = await fetch(`${API}/api/tickets`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          property_id: newTicket.property_id,
          subject: newTicket.subject,
          message: newTicket.message,
          priority: newTicket.priority,
          recipients: newTicket.recipients,
          category: newTicket.category || null,
          attachments: newTicket.attachments
        })
      });

      const data = await res.json();

      if (res.ok) {
        toast.success('Ticket creato con successo');
        setShowNewTicket(false);
        setNewTicket({
          property_id: '',
          subject: '',
          message: '',
          priority: 'normale',
          recipients: ['ufficio'],
          category: '',
          attachments: []
        });
        setSelectedProperty(null);
        fetchData();
      } else {
        toast.error(data.detail || 'Errore nella creazione del ticket');
      }
    } catch (error) {
      toast.error('Errore di connessione');
    } finally {
      setSendingTicket(false);
    }
  };

  const loadTicketDetail = async (ticketId) => {
    try {
      const res = await fetch(`${API}/api/tickets/${ticketId}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (res.ok) {
        const data = await res.json();
        setSelectedTicket(data.ticket);
        setTicketMessages(data.messages);
        // Smart reply recipients dal backend
        if (data.ticket.suggested_reply_recipients) {
          setReplyRecipients(data.ticket.suggested_reply_recipients);
        } else {
          setReplyRecipients(['ufficio']);
        }
        
        // Aggiorna la lista ticket per riflettere i messaggi letti
        // e triggera aggiornamento badge sidebar
        fetchData();
        
        // Emetti evento per aggiornare badge sidebar
        window.dispatchEvent(new CustomEvent('ticketRead'));
      }
    } catch (error) {
      console.error('Error:', error);
    }
  };

  const handleSendReply = async () => {
    if (!replyContent.trim() || !selectedTicket) return;
    if (replyRecipients.length === 0) {
      toast.error('Seleziona almeno un destinatario');
      return;
    }

    setSendingReply(true);
    try {
      const res = await fetch(`${API}/api/tickets/${selectedTicket.id}/messages`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          content: replyContent,
          recipients: replyRecipients,
          attachments: replyAttachments
        })
      });

      const data = await res.json();

      if (res.ok) {
        toast.success('Messaggio inviato');
        setReplyContent('');
        setReplyAttachments([]);
        loadTicketDetail(selectedTicket.id);
        fetchData();
      } else {
        toast.error(data.detail || 'Errore invio messaggio');
      }
    } catch (error) {
      toast.error('Errore di connessione');
    } finally {
      setSendingReply(false);
    }
  };

  // Funzione per chiudere/riaprire ticket
  const handleToggleTicketStatus = async () => {
    if (!selectedTicket) return;
    
    const newStatus = selectedTicket.status === 'chiuso' ? 'aperto' : 'chiuso';
    
    try {
      const res = await fetch(`${API}/api/tickets/${selectedTicket.id}/status`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({ status: newStatus })
      });
      
      if (res.ok) {
        toast.success(newStatus === 'chiuso' ? 'Ticket chiuso' : 'Ticket riaperto');
        fetchData();
        loadTicketDetail(selectedTicket.id);
      } else {
        const data = await res.json();
        toast.error(data.detail || 'Errore nel cambio stato');
      }
    } catch (error) {
      toast.error('Errore di connessione');
    }
  };

  const getStatusBadge = (status) => {
    const badges = {
      aperto: { color: 'bg-green-100 text-green-700', icon: Clock, label: 'Aperto' },
      chiuso: { color: 'bg-slate-100 text-slate-600', icon: CheckCircle, label: 'Chiuso' },
      sospeso: { color: 'bg-yellow-100 text-yellow-700', icon: Pause, label: 'Sospeso' }
    };
    const badge = badges[status] || badges.aperto;
    const Icon = badge.icon;
    return (
      <span className={`inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium ${badge.color}`}>
        <Icon className="h-3 w-3" />
        {badge.label}
      </span>
    );
  };

  const getPriorityBadge = (priority) => {
    if (priority === 'urgente') {
      return (
        <span className="inline-flex items-center gap-1 px-2 py-1 bg-red-100 text-red-700 rounded-full text-xs font-medium">
          <AlertTriangle className="h-3 w-3" />
          Urgente
        </span>
      );
    }
    return null;
  };

  const getRecipientLabel = (recipient) => {
    const labels = {
      proprieta: 'Proprietario',
      ufficio: 'Ufficio',
      partner: 'Partner',
      responsabile: 'Responsabile',
      entrambi: 'Entrambi'
    };
    return labels[recipient] || recipient;
  };

  const getSenderStyle = (senderType) => {
    switch (senderType) {
      case 'responsabile':
        return 'bg-blue-600 text-white';
      case 'proprieta':
        return 'bg-green-100 text-green-800';
      case 'ufficio':
        return 'bg-purple-100 text-purple-800';
      default:
        return 'bg-slate-100 text-slate-800';
    }
  };

  const getSenderLabel = (senderType) => {
    switch (senderType) {
      case 'responsabile':
        return 'Tu';
      case 'proprieta':
        return 'Proprietario';
      case 'ufficio':
        return 'Ufficio HWsmart';
      default:
        return senderType;
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="h-8 w-8 animate-spin text-blue-600" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="apri-ticket-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-slate-800 flex items-center gap-2">
            <Ticket className="h-6 w-6 text-blue-600" />
            I Miei Ticket
          </h1>
          <p className="text-slate-500 mt-1">Comunicazioni con proprietari e ufficio</p>
        </div>
        <Button 
          onClick={handleOpenNewTicket} 
          className="bg-blue-600 hover:bg-blue-700"
          data-testid="new-ticket-btn"
        >
          <Plus className="h-4 w-4 mr-2" />
          Nuovo Ticket
        </Button>
      </div>

      {/* Card tab: Aperti / Chiusi / Comunicazioni da Ufficio */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3" data-testid="ticket-tabs">
        {[
          { key: 'aperti', label: 'Aperti / In Lavorazione', Icon: MessageSquare, desc: 'Ticket in corso' },
          { key: 'chiusi', label: 'Chiusi', Icon: Archive, desc: 'Ticket completati' },
          { key: 'comunicazioni', label: 'Comunicazioni da Ufficio', Icon: Megaphone, desc: "Avvisi e news dall'ufficio" }
        ].map(({ key, label, Icon, desc }) => {
          const bucket = tickets.filter(t => bucketOf(t) === key);
          const unread = bucket.filter(t => (t.unread_by_responsible || 0) > 0).length;
          const active = activeTab === key;
          return (
            <button
              key={key}
              onClick={() => { setActiveTab(key); setFilterStatus(''); }}
              className={`relative text-left p-4 rounded-xl transition-all text-white ${
                active
                  ? 'bg-[#1D4ED8] shadow-lg ring-2 ring-blue-400 ring-offset-2'
                  : 'bg-[#1E3A8A] hover:bg-[#24479e] shadow-md'
              }`}
              data-testid={`tab-card-${key}`}
            >
              {unread > 0 && (
                <span
                  className="absolute right-3 top-1/2 -translate-y-1/2 min-w-[38px] h-[38px] px-2 bg-red-500 text-white rounded-full flex items-center justify-center text-lg font-extrabold shadow-lg ring-2 ring-white/70 animate-pulse"
                  data-testid={`tab-unread-${key}`}
                >
                  {unread}
                </span>
              )}
              <div className="flex items-center gap-3">
                <div className="p-2.5 rounded-lg shrink-0 bg-white/15">
                  <Icon className="h-5 w-5 text-white" />
                </div>
                <div className="min-w-0">
                  <p className="text-2xl font-bold leading-none text-white">{bucket.length}</p>
                  <p className="text-sm font-medium mt-1 truncate text-white">{label}</p>
                  <p className="text-xs text-blue-200 truncate">{desc}</p>
                </div>
              </div>
            </button>
          );
        })}
      </div>

      {/* Form Nuovo Ticket */}
      {showNewTicket && (
        <Card className="p-6 border-blue-200 bg-blue-50/30" data-testid="new-ticket-form">
          <h3 className="text-lg font-semibold text-slate-800 mb-4 flex items-center gap-2">
            <Ticket className="h-5 w-5 text-blue-600" />
            Crea Nuovo Ticket
          </h3>
          <form onSubmit={handleCreateTicket} className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <Label>Immobile *</Label>
                <div className="relative mt-1">
                  <Input
                    type="text"
                    placeholder="Cerca immobile per nome o codice..."
                    value={propertySearch}
                    onChange={(e) => setPropertySearch(e.target.value)}
                    className="bg-white border-slate-300 pr-8"
                    data-testid="search-property"
                  />
                  <Building2 className="absolute right-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
                </div>
                {/* Lista filtrata immobili */}
                {propertySearch.length > 0 && (
                  <div className="mt-1 max-h-48 overflow-y-auto border border-slate-200 rounded-lg bg-white shadow-lg">
                    {properties
                      .filter(p => {
                        const search = propertySearch.toLowerCase();
                        return p.name?.toLowerCase().includes(search) || 
                               p.property_code?.toLowerCase().includes(search);
                      })
                      .slice(0, 10)
                      .map(p => (
                        <button
                          key={p.id}
                          type="button"
                          onClick={() => {
                            setNewTicket({ ...newTicket, property_id: p.id });
                            setPropertySearch(`${p.name} (${p.property_code})`);
                            setSelectedProperty(p);
                          }}
                          className={`w-full px-3 py-2 text-left hover:bg-blue-50 border-b last:border-b-0 text-sm ${
                            newTicket.property_id === p.id ? 'bg-blue-100' : ''
                          }`}
                        >
                          <div className="font-medium text-slate-800 notranslate" translate="no">{p.name}</div>
                          <div className="text-xs text-slate-500">{p.property_code}</div>
                        </button>
                      ))
                    }
                    {properties.filter(p => {
                      const search = propertySearch.toLowerCase();
                      return p.name?.toLowerCase().includes(search) || 
                             p.property_code?.toLowerCase().includes(search);
                    }).length === 0 && (
                      <div className="px-3 py-2 text-sm text-slate-500">
                        Nessun immobile trovato
                      </div>
                    )}
                  </div>
                )}
                {/* Mostra immobile selezionato */}
                {newTicket.property_id && !propertySearch && (
                  <div className="mt-1 text-sm text-blue-600">
                    Selezionato: {properties.find(p => p.id === newTicket.property_id)?.name || 'N/A'}
                  </div>
                )}
              </div>
              <div>
                <Label>Destinatari *</Label>
                <div className="mt-2 space-y-2">
                  <label className="flex items-center gap-3 p-3 border rounded-lg cursor-pointer hover:bg-slate-50 transition">
                    <input
                      type="checkbox"
                      checked={newTicket.recipients.includes('ufficio')}
                      onChange={(e) => {
                        const checked = e.target.checked;
                        setNewTicket(prev => ({
                          ...prev,
                          recipients: checked 
                            ? [...prev.recipients, 'ufficio']
                            : prev.recipients.filter(r => r !== 'ufficio')
                        }));
                      }}
                      className="w-5 h-5 rounded border-slate-300"
                    />
                    <div className="flex items-center gap-2">
                      <Building className="h-5 w-5 text-blue-600" />
                      <span className="font-medium">Ufficio HWsmart</span>
                    </div>
                  </label>
                  
                  <label className="flex items-center gap-3 p-3 border rounded-lg cursor-pointer hover:bg-slate-50 transition">
                    <input
                      type="checkbox"
                      checked={newTicket.recipients.includes('proprieta')}
                      onChange={(e) => {
                        const checked = e.target.checked;
                        setNewTicket(prev => ({
                          ...prev,
                          recipients: checked 
                            ? [...prev.recipients, 'proprieta']
                            : prev.recipients.filter(r => r !== 'proprieta')
                        }));
                      }}
                      className="w-5 h-5 rounded border-slate-300"
                    />
                    <div className="flex items-center gap-2">
                      <Users className="h-5 w-5 text-green-600" />
                      <span className="font-medium">Proprietario</span>
                    </div>
                  </label>
                  
                  <label className="flex items-center gap-3 p-3 border rounded-lg cursor-pointer hover:bg-slate-50 transition">
                    <input
                      type="checkbox"
                      checked={newTicket.recipients.includes('partner')}
                      onChange={(e) => {
                        const checked = e.target.checked;
                        setNewTicket(prev => ({
                          ...prev,
                          recipients: checked 
                            ? [...prev.recipients, 'partner']
                            : prev.recipients.filter(r => r !== 'partner')
                        }));
                      }}
                      className="w-5 h-5 rounded border-slate-300"
                    />
                    <div className="flex items-center gap-2">
                      <Building2 className="h-5 w-5 text-purple-600" />
                      <span className="font-medium">Partner</span>
                    </div>
                  </label>
                </div>
                
                {/* Error se nessun destinatario */}
                {newTicket.recipients.length === 0 && (
                  <div className="mt-2 p-3 bg-red-50 border border-red-200 rounded-lg text-xs text-red-800">
                    <strong>Errore:</strong> Seleziona almeno un destinatario.
                  </div>
                )}
                
                {/* Disclaimer se Ufficio NON selezionato */}
                {newTicket.recipients.length > 0 && !newTicket.recipients.includes('ufficio') && (
                  <div className="mt-2 p-3 bg-amber-50 border border-amber-300 rounded-lg text-xs text-amber-800">
                    <div className="flex gap-2">
                      <AlertTriangle className="h-4 w-4 flex-shrink-0 mt-0.5" />
                      <div>
                        <strong>⚠️ Attenzione:</strong> Stai inviando questo ticket senza coinvolgere l'Ufficio HWsmart.
                        <br />
                        <span className="font-semibold">HWsmart non sarà responsabile di eventuali accordi presi privatamente tra le parti senza il coinvolgimento dell'ufficio.</span>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <Label>Oggetto *</Label>
                <Input
                  value={newTicket.subject}
                  onChange={(e) => setNewTicket({ ...newTicket, subject: e.target.value })}
                  placeholder="Es: Problema caldaia, Richiesta manutenzione..."
                  required
                  className="bg-white border-slate-300"
                  data-testid="input-subject"
                />
              </div>
              <div>
                <Label>Categoria</Label>
                <select
                  value={newTicket.category}
                  onChange={(e) => setNewTicket({ ...newTicket, category: e.target.value })}
                  className="w-full mt-1 px-3 py-2 border border-slate-300 rounded-lg bg-white focus:ring-2 focus:ring-blue-500"
                  data-testid="select-category"
                >
                  <option value="">Seleziona categoria...</option>
                  <optgroup label="Problemi Immobile">
                    {categories.filter(c => c.group === 'immobile').map(c => (
                      <option key={c.value} value={c.value}>{c.label}</option>
                    ))}
                  </optgroup>
                  <optgroup label="Problemi Gestione">
                    {categories.filter(c => c.group === 'responsabile').map(c => (
                      <option key={c.value} value={c.value}>{c.label}</option>
                    ))}
                  </optgroup>
                  <optgroup label="Holiday World">
                    {categories.filter(c => c.group === 'holidayworld').map(c => (
                      <option key={c.value} value={c.value}>{c.label}</option>
                    ))}
                  </optgroup>
                  <optgroup label="Altro">
                    {categories.filter(c => c.group === 'altro').map(c => (
                      <option key={c.value} value={c.value}>{c.label}</option>
                    ))}
                  </optgroup>
                </select>
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <Label>Priorità</Label>
                <div className="flex gap-4 mt-2">
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input
                      type="radio"
                      name="priority"
                      value="normale"
                      checked={newTicket.priority === 'normale'}
                      onChange={(e) => setNewTicket({ ...newTicket, priority: e.target.value })}
                      className="w-4 h-4"
                    />
                    <span className="text-sm">Normale</span>
                  </label>
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input
                      type="radio"
                      name="priority"
                      value="urgente"
                      checked={newTicket.priority === 'urgente'}
                      onChange={(e) => setNewTicket({ ...newTicket, priority: e.target.value })}
                      className="w-4 h-4"
                    />
                    <span className="text-sm text-red-600 font-medium flex items-center gap-1">
                      <AlertTriangle className="h-4 w-4" />
                      Urgente
                    </span>
                  </label>
                </div>
              </div>
            </div>

            <div>
              <Label>Messaggio *</Label>
              <Textarea
                value={newTicket.message}
                onChange={(e) => setNewTicket({ ...newTicket, message: e.target.value })}
                placeholder="Descrivi il problema o la richiesta..."
                rows={4}
                required
                className="bg-white border-slate-300"
                data-testid="input-message"
              />
              <p className="text-xs text-slate-500 mt-1">
                Non includere nomi, numeri di telefono o email nel messaggio
              </p>
            </div>

            {/* Allegati */}
            <div>
              <Label>Allega Foto</Label>
              <div className="mt-2 flex flex-wrap gap-2">
                {/* Input nascosto per fotocamera */}
                <input
                  type="file"
                  ref={cameraInputRef}
                  onChange={(e) => handleImageUpload(e, false)}
                  accept="image/*"
                  capture="environment"
                  className="hidden"
                />
                {/* Input nascosto per galleria */}
                <input
                  type="file"
                  ref={fileInputRef}
                  onChange={(e) => handleImageUpload(e, false)}
                  accept="image/*"
                  multiple
                  className="hidden"
                />
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => cameraInputRef.current?.click()}
                  className="gap-2 bg-white"
                >
                  <Camera className="h-4 w-4" />
                  Scatta Foto
                </Button>
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => fileInputRef.current?.click()}
                  className="gap-2 bg-white"
                >
                  <Upload className="h-4 w-4" />
                  Carica da Galleria
                </Button>
              </div>
              <p className="text-xs text-slate-500 mt-1">
                Max 5 foto. Le immagini non devono contenere dati personali.
              </p>
              {newTicket.attachments.length > 0 && (
                <div className="flex gap-2 mt-3 flex-wrap">
                  {newTicket.attachments.map((img, idx) => (
                    <div key={idx} className="relative">
                      <img
                        src={img}
                        alt={`Allegato ${idx + 1}`}
                        className="h-20 w-20 object-cover rounded-lg border"
                      />
                      <button
                        type="button"
                        onClick={() => removeAttachment(idx, false)}
                        className="absolute -top-2 -right-2 bg-red-500 text-white rounded-full p-1 hover:bg-red-600"
                      >
                        <X className="h-3 w-3" />
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>

            <div className="flex gap-2 justify-end pt-2">
              <Button type="button" variant="outline" onClick={() => setShowNewTicket(false)}>
                Annulla
              </Button>
              <Button 
                type="submit" 
                disabled={sendingTicket} 
                className="bg-blue-600 hover:bg-blue-700"
                data-testid="submit-ticket-btn"
              >
                {sendingTicket ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Send className="h-4 w-4 mr-2" />}
                Invia Ticket
              </Button>
            </div>
          </form>
        </Card>
      )}

      {/* Layout principale */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Lista Ticket */}
        <div className="lg:col-span-1 space-y-3">
          <div className="flex items-center justify-between">
            <h3 className="font-semibold text-slate-700">Ticket</h3>
            <div className="flex gap-2">
              <Button variant="ghost" size="sm" onClick={fetchData}>
                <RefreshCw className="h-4 w-4" />
              </Button>
              <Button 
                variant={viewMode === 'list' ? 'default' : 'outline'} 
                size="sm" 
                onClick={() => setViewMode('list')}
              >
                Lista
              </Button>
              <Button 
                variant={viewMode === 'table' ? 'default' : 'outline'} 
                size="sm" 
                onClick={() => setViewMode('table')}
              >
                Tabella
              </Button>
            </div>
          </div>
          
          {/* Filtri */}
          <div className="flex flex-wrap gap-2 mb-3">
            <Input
              placeholder="Cerca..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full sm:w-40 h-8 text-sm"
            />
            <select
              value={filterStatus}
              onChange={(e) => setFilterStatus(e.target.value)}
              className="border rounded px-2 py-1 text-sm h-8"
            >
              <option value="">Tutti stati</option>
              <option value="aperto">Aperto</option>
              <option value="in_attesa">In attesa</option>
              <option value="chiuso">Chiuso</option>
            </select>
            <select
              value={filterProperty}
              onChange={(e) => setFilterProperty(e.target.value)}
              className="border rounded px-2 py-1 text-sm h-8"
            >
              <option value="">Tutti immobili</option>
              {properties.map(p => (
                <option key={p.id} value={p.id}>{p.property_code || p.name}</option>
              ))}
            </select>
            <select
              value={filterCategory}
              onChange={(e) => setFilterCategory(e.target.value)}
              className="border rounded px-2 py-1 text-sm h-8"
            >
              <option value="">Tutte categorie</option>
              {Object.entries(TICKET_CATEGORIES).map(([key, val]) => (
                <option key={key} value={key}>{val.label}</option>
              ))}
            </select>
          </div>

          {filteredTickets.length === 0 ? (
            <Card className="p-8 text-center">
              <Ticket className="h-12 w-12 text-slate-300 mx-auto mb-3" />
              <p className="text-slate-500">Nessun ticket trovato</p>
              <Button 
                onClick={handleOpenNewTicket} 
                className="mt-4 bg-blue-600"
                data-testid="empty-new-ticket-btn"
              >
                Crea il primo ticket
              </Button>
            </Card>
          ) : viewMode === 'table' ? (
            /* Vista Tabella */
            <Card className="overflow-hidden">
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead className="bg-slate-50 border-b">
                    <tr>
                      <th className="px-3 py-2 text-left cursor-pointer hover:bg-slate-100" onClick={() => handleSort('created_at')}>
                        Data {sortConfig.key === 'created_at' && (sortConfig.direction === 'asc' ? '↑' : '↓')}
                      </th>
                      <th className="px-3 py-2 text-left cursor-pointer hover:bg-slate-100" onClick={() => handleSort('subject')}>
                        Oggetto {sortConfig.key === 'subject' && (sortConfig.direction === 'asc' ? '↑' : '↓')}
                      </th>
                      <th className="px-3 py-2 text-left cursor-pointer hover:bg-slate-100" onClick={() => handleSort('property_name')}>
                        Immobile {sortConfig.key === 'property_name' && (sortConfig.direction === 'asc' ? '↑' : '↓')}
                      </th>
                      <th className="px-3 py-2 text-left">Categoria</th>
                      <th className="px-3 py-2 text-left cursor-pointer hover:bg-slate-100" onClick={() => handleSort('status')}>
                        Stato {sortConfig.key === 'status' && (sortConfig.direction === 'asc' ? '↑' : '↓')}
                      </th>
                    </tr>
                  </thead>
                  <tbody className="divide-y">
                    {filteredTickets.map(t => (
                      <tr 
                        key={t.id}
                        className={`cursor-pointer hover:bg-slate-50 ${
                          selectedTicket?.id === t.id ? 'bg-blue-50' : ''
                        } ${t.unread_by_responsible > 0 ? 'font-medium' : ''}`}
                        onClick={() => loadTicketDetail(t.id)}
                      >
                        <td className="px-3 py-2 whitespace-nowrap">
                          {new Date(t.created_at).toLocaleDateString('it-IT', {day: '2-digit', month: 'short'})}
                        </td>
                        <td className="px-3 py-2">
                          <div className="flex items-center gap-2">
                            {t.priority === 'urgente' && <AlertTriangle className="h-3 w-3 text-red-500" />}
                            <span className="truncate max-w-[150px]">{t.subject}</span>
                            {t.unread_by_responsible > 0 && (
                              <span className="bg-red-500 text-white text-xs px-1.5 rounded-full">{t.unread_by_responsible}</span>
                            )}
                          </div>
                        </td>
                        <td className="px-3 py-2 text-slate-600 truncate max-w-[120px]">{t.property_name}</td>
                        <td className="px-3 py-2">{getCategoryBadge(t.category)}</td>
                        <td className="px-3 py-2">{getStatusBadge(t.status)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </Card>
          ) : (
            /* Vista Lista Card */
            filteredTickets.map((t) => (
              <Card
                key={t.id}
                className={`p-4 cursor-pointer transition-all border-l-4 ${
                  selectedTicket?.id === t.id
                    ? 'border-l-blue-500 bg-blue-50 shadow-md border-blue-300'
                    : t.status === 'chiuso'
                      ? 'border-l-green-500 bg-green-50/30 hover:bg-green-50 hover:shadow-sm'
                      : 'border-l-red-500 bg-red-50/30 hover:bg-red-50 hover:shadow-sm'
                }`}
                onClick={() => loadTicketDetail(t.id)}
                data-testid={`ticket-item-${t.id}`}
              >
                <div className="flex items-start justify-between gap-2">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-1 flex-wrap">
                      {getStatusBadge(t.status)}
                      {getPriorityBadge(t.priority)}
                    </div>
                    <p className="font-medium text-slate-800 truncate">{t.subject}</p>
                    <p className="text-xs text-slate-500 flex items-center gap-1 mt-1">
                      <Building2 className="h-3 w-3" />
                      {t.property_name}
                    </p>
                  </div>
                  <div className="flex flex-col items-end gap-1">
                    {t.unread_by_responsible > 0 && (
                      <span className="bg-red-500 text-white text-xs px-2 py-0.5 rounded-full">
                        {t.unread_by_responsible}
                      </span>
                    )}
                    <ChevronRight className="h-4 w-4 text-slate-400" />
                  </div>
                </div>
                <p className="text-xs text-slate-400 mt-2">
                  {new Date(t.created_at).toLocaleDateString('it-IT', {
                    day: '2-digit',
                    month: 'short',
                    hour: '2-digit',
                    minute: '2-digit'
                  })}
                </p>
              </Card>
            ))
          )}
        </div>

        {/* Dettaglio Ticket */}
        <div className="lg:col-span-2">
          {selectedTicket ? (
            <Card className="flex flex-col h-[600px]" data-testid="ticket-detail">
              {/* Header */}
              <div className="p-4 border-b bg-slate-50">
                <div className="flex items-start justify-between">
                  <div>
                    <div className="flex items-center gap-2 mb-1">
                      {getStatusBadge(selectedTicket.status)}
                      {getPriorityBadge(selectedTicket.priority)}
                    </div>
                    <h3 className="font-semibold text-slate-800">{selectedTicket.subject}</h3>
                    <p className="text-sm text-slate-500 flex items-center gap-1 mt-1">
                      <Building2 className="h-4 w-4" />
                      {selectedTicket.property_name}
                    </p>
                    {/* Mostra destinatari */}
                    {selectedTicket.recipients?.length > 0 && (
                      <p className="text-xs text-slate-500 mt-1 flex items-center gap-1">
                        <Users className="h-3 w-3" />
                        <span>Inviato a: </span>
                        {selectedTicket.recipients.map((r, i) => (
                          <span key={r} className="inline-flex items-center px-1.5 py-0.5 rounded bg-slate-100 text-slate-600 text-xs">
                            {getRecipientLabel(r)}{i < selectedTicket.recipients.length - 1 ? '' : ''}
                          </span>
                        ))}
                      </p>
                    )}
                  </div>
                  <div className="flex items-center gap-2">
                    <span className="text-xs text-slate-400 bg-slate-100 px-2 py-1 rounded">
                      {selectedTicket.message_count} messaggi
                    </span>
                    <Button
                      variant={selectedTicket.status === 'chiuso' ? 'outline' : 'destructive'}
                      size="sm"
                      onClick={handleToggleTicketStatus}
                      data-testid="toggle-ticket-status-btn"
                    >
                      {selectedTicket.status === 'chiuso' ? (
                        <>
                          <RotateCcw className="h-4 w-4 mr-1" />
                          Riapri
                        </>
                      ) : (
                        <>
                          <XCircle className="h-4 w-4 mr-1" />
                          Chiudi
                        </>
                      )}
                    </Button>
                  </div>
                </div>
              </div>

              {/* Messaggi */}
              <div className="flex-1 overflow-y-auto p-4 space-y-4">
                {ticketMessages.map((msg) => (
                  <div
                    key={msg.id}
                    className={`flex ${msg.sender_type === 'responsabile' ? 'justify-end' : 'justify-start'}`}
                  >
                    <div className={`max-w-[80%] min-w-0 rounded-lg p-3 ${getSenderStyle(msg.sender_type)}`}>
                      <p className="text-xs font-medium mb-1 opacity-80">
                        {getSenderLabel(msg.sender_type)}
                      </p>
                      <p className="text-sm whitespace-pre-wrap break-words [overflow-wrap:anywhere]">{msg.content}</p>
                      
                      {/* Allegati */}
                      {msg.attachments?.length > 0 && (
                        <div className="flex gap-2 mt-2 flex-wrap">
                          {msg.attachments.map((img, idx) => (
                            <img
                              key={idx}
                              src={img}
                              alt={`Allegato ${idx + 1}`}
                              className="h-24 w-24 object-cover rounded border cursor-pointer hover:opacity-80"
                              onClick={() => openAttachment(img)}
                            />
                          ))}
                        </div>
                      )}
                      
                      <p className="text-xs mt-2 opacity-60">
                        {new Date(msg.sent_at).toLocaleString('it-IT', {
                          day: '2-digit',
                          month: 'short',
                          hour: '2-digit',
                          minute: '2-digit'
                        })}
                      </p>
                    </div>
                  </div>
                ))}
              </div>

              {/* Input risposta */}
              {selectedTicket.status !== 'chiuso' ? (
                <div className="p-4 border-t bg-white space-y-3">
                  {/* Destinatari risposta */}
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="text-xs text-slate-500">Invia a:</span>
                    <label className="flex items-center gap-1 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={replyRecipients.includes('ufficio')}
                        onChange={() => setReplyRecipients(prev => prev.includes('ufficio') ? prev.filter(r => r !== 'ufficio') : [...prev, 'ufficio'])}
                        className="w-3.5 h-3.5 rounded"
                      />
                      <span className="text-xs">Ufficio</span>
                    </label>
                    <label className="flex items-center gap-1 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={replyRecipients.includes('proprieta')}
                        onChange={() => setReplyRecipients(prev => prev.includes('proprieta') ? prev.filter(r => r !== 'proprieta') : [...prev, 'proprieta'])}
                        className="w-3.5 h-3.5 rounded"
                      />
                      <span className="text-xs">Proprietario</span>
                    </label>
                    <label className="flex items-center gap-1 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={replyRecipients.includes('partner')}
                        onChange={() => setReplyRecipients(prev => prev.includes('partner') ? prev.filter(r => r !== 'partner') : [...prev, 'partner'])}
                        className="w-3.5 h-3.5 rounded"
                      />
                      <span className="text-xs">Partner</span>
                    </label>
                  </div>
                  
                  {/* Allegati risposta */}
                  {replyAttachments.length > 0 && (
                    <div className="flex gap-2 mb-2 flex-wrap">
                      {replyAttachments.map((img, idx) => (
                        <div key={idx} className="relative">
                          <img
                            src={img}
                            alt={`Allegato ${idx + 1}`}
                            className="h-16 w-16 object-cover rounded border"
                          />
                          <button
                            onClick={() => removeAttachment(idx, true)}
                            className="absolute -top-1 -right-1 bg-red-500 text-white rounded-full p-0.5"
                          >
                            <X className="h-3 w-3" />
                          </button>
                        </div>
                      ))}
                    </div>
                  )}
                  
                  <div className="flex gap-2">
                    {/* Input nascosto per fotocamera risposta */}
                    <input
                      type="file"
                      ref={replyCameraInputRef}
                      onChange={(e) => handleImageUpload(e, true)}
                      accept="image/*"
                      capture="environment"
                      className="hidden"
                    />
                    {/* Input nascosto per galleria risposta */}
                    <input
                      type="file"
                      ref={replyFileInputRef}
                      onChange={(e) => handleImageUpload(e, true)}
                      accept="image/*"
                      multiple
                      className="hidden"
                    />
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => replyCameraInputRef.current?.click()}
                      title="Scatta foto"
                    >
                      <Camera className="h-4 w-4" />
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => replyFileInputRef.current?.click()}
                      title="Carica da galleria"
                    >
                      <Upload className="h-4 w-4" />
                    </Button>
                    <Textarea
                      value={replyContent}
                      onChange={(e) => setReplyContent(e.target.value)}
                      placeholder="Scrivi una risposta..."
                      rows={2}
                      className="flex-1 bg-white border-slate-300"
                      data-testid="reply-input"
                    />
                    <Button
                      onClick={handleSendReply}
                      disabled={!replyContent.trim() || sendingReply || replyRecipients.length === 0}
                      className="bg-blue-600 hover:bg-blue-700"
                      data-testid="send-reply-btn"
                    >
                      {sendingReply ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
                    </Button>
                  </div>
                </div>
              ) : (
                <div className="p-4 border-t bg-slate-50 text-center text-slate-500 text-sm">
                  Questo ticket è stato chiuso
                </div>
              )}
            </Card>
          ) : (
            <Card className="h-[600px] flex flex-col items-center justify-center text-center p-8">
              <MessageSquare className="h-16 w-16 text-slate-200 mb-4" />
              <p className="text-slate-500">Seleziona un ticket per visualizzare i messaggi</p>
              <p className="text-slate-400 text-sm mt-2">oppure crea un nuovo ticket</p>
            </Card>
          )}
        </div>
      </div>
    </div>
  );
};

export default ApriTicket;
